// ============================================
// Challenge Page — /challenge/:movie
// ============================================
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import CTAButton from '../../components/CTAButton/CTAButton';
import CoinBadge from '../../components/CoinBadge/CoinBadge';
import LoadingSkeleton from '../../components/LoadingSkeleton/LoadingSkeleton';
import ShareButtons from '../../components/ShareButtons/ShareButtons';
import StickyMobileCTA from '../../components/StickyMobileCTA/StickyMobileCTA';
import { MediaService } from '../../services/mediaService';
import { getChallengeUrl } from '../../utils/helpers';
import styles from './Challenge.module.css';

const MOCK_CHALLENGER = {
    username: 'alexmovies',
    score: 8,
    total: 8,
    coinTier: 'gold',
};

/**
 * Fetch media details from backend
 * @param {string} movieSlug - Movie slug (e.g., "interstellar-550")
 * @returns {Promise<Object>}
 */
async function fetchMediaDetails(movieSlug) {
    // Extract TMDB ID from slug (format: "title-123")
    const parts = movieSlug.split('-');
    const tmdbId = parts[parts.length - 1];

    // For now, assume it's a movie. You could add logic to detect TV shows
    const kind = 'movie';

    try {
        const details = await MediaService.getDetails(kind, tmdbId);

        // Transform API response to match component expectations
        return {
            title: details.title || details.name,
            backdrop: details.backdrop_path
                ? `https://image.tmdb.org/t/p/original${details.backdrop_path}`
                : 'https://via.placeholder.com/1920x1080?text=No+Backdrop',
            poster: details.poster_path
                ? `https://image.tmdb.org/t/p/w500${details.poster_path}`
                : 'https://via.placeholder.com/500x750?text=No+Poster',
            year: (details.release_date || details.first_air_date || '').split('-')[0],
            rating: details.vote_average?.toFixed(1),
            overview: details.overview,
            quizId: tmdbId, // Use TMDB ID as quiz ID for now
            tmdbId: tmdbId,
            kind: kind,
        };
    } catch (error) {
        console.error('Error fetching media details:', error);
        throw error;
    }
}

function Challenge() {
    const { movie: movieSlug } = useParams();
    const [movie, setMovie] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchMediaDetails(movieSlug)
            .then(setMovie)
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [movieSlug]);

    if (loading) return <ChallengeSkeleton />;
    if (!movie) {
        return (
            <div className={styles.errorState}>
                <span style={{ fontSize: '2.5rem' }}>🎬</span>
                <p>Challenge not found.</p>
                <Link to="/" className={styles.errorLink}>← Back to Home</Link>
            </div>
        );
    }

    const earnedBadge = {
        name: `${movie.title} Coin`,
        movie: movie.title,
        tier: MOCK_CHALLENGER.coinTier,
        emoji: '🏆',
    };

    const challengeUrl = getChallengeUrl(movieSlug);
    const shareTitle = `@${MOCK_CHALLENGER.username} got ${MOCK_CHALLENGER.score}/${MOCK_CHALLENGER.total} on the ${movie.title} challenge on Aluvo. Can you beat it?`;

    return (
        <main className={styles.page}>
            <StickyMobileCTA href="#download" />

            {/* Backdrop */}
            <div className={styles.backdrop} aria-hidden="true">
                <img src={movie.backdrop} alt="" className={styles.backdropImg} />
                <div className={styles.backdropOverlay} />
            </div>

            <div className={`container ${styles.inner}`}>
                {/* ── Coin hero ── */}
                <div className={styles.coinHero}>
                    <CoinBadge badge={earnedBadge} size="xl" showLabel={false} />
                </div>

                {/* ── Challenge message ── */}
                <div className={styles.content}>
                    <div className={styles.proofBadge}>✅ Verified Watch</div>

                    <h1 className={styles.headline}>
                        <Link
                            to={`/user/${MOCK_CHALLENGER.username}`}
                            className={styles.username}
                        >
                            {MOCK_CHALLENGER.display_name}
                        </Link>
                        {' '}
                        <span className={styles.usernameHandle}>@{MOCK_CHALLENGER.username}</span>
                        {' '}proved
                        <br />they watched
                        <span className={styles.movieTitle}> {movie.title}</span>
                    </h1>

                    <p className={styles.score}>
                        {MOCK_CHALLENGER.score}/{MOCK_CHALLENGER.total} correct · Gold tier earned 🥇
                    </p>

                    <p className={styles.challenge}>Think you can beat it?</p>

                    {/* ── What is Aluvo (moved up — first-time visitors need context) ── */}
                    <div className={styles.explainer}>
                        <div className={styles.explainerSteps}>
                            <span>🔖 Save</span>
                            <span className={styles.explainerArrow}>→</span>
                            <span>🧠 Watch &amp; Prove It</span>
                            <span className={styles.explainerArrow}>→</span>
                            <span>🪙 Earn a Coin</span>
                        </div>
                        <p className={styles.explainerText}>
                            Aluvo lets you save what you want to watch, verify you watched it with a short challenge, and earn a collectible coin. Every coin is earned — never bought.
                        </p>
                    </div>

                    {/* CTAs — primary first, secondary second */}
                    <div className={styles.ctas}>
                        <CTAButton variant="gold" size="lg" icon="🧠">
                            <Link to={`/quiz/${movie.quizId}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                                Take the Challenge
                            </Link>
                        </CTAButton>
                        <CTAButton variant="secondary" size="lg" href="#download">
                            Join Aluvo Free
                        </CTAButton>
                    </div>

                    {/* Share */}
                    <div className={styles.shareRow}>
                        <ShareButtons
                            url={challengeUrl}
                            title={shareTitle}
                            label="Share this challenge:"
                        />
                    </div>
                </div>

                {/* ── What is Aluvo ── */}
                <div className={styles.explainer} id="download">
                    <h2 className={styles.explainerTitle}>What is Aluvo?</h2>
                    <p className={styles.explainerText}>
                        Aluvo is the social watchlist platform where you earn collectible coins by proving
                        you actually watched. Track, share, and challenge your friends.
                    </p>
                    <CTAButton variant="dark" size="md" href="#download">
                        Download the App →
                    </CTAButton>
                </div>
            </div>
        </main>
    );
}

function ChallengeSkeleton() {
    return (
        <div className={styles.page} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 28 }}>
                <LoadingSkeleton width="160px" height="160px" borderRadius="50%" />
                <LoadingSkeleton width="340px" height="36px" />
                <LoadingSkeleton width="240px" height="20px" />
                <LoadingSkeleton width="200px" height="44px" borderRadius="9999px" />
            </div>
        </div>
    );
}

export default Challenge;